import React, { FC } from "react";
import { Avatar, Progress, Tooltip } from "antd";
// No chart glyph exists in the app set, so View Results stays on antd.
import { UserOutlined, BarChartOutlined } from "@ant-design/icons";
import moment from "moment";
import { BeamSurvey } from "./types";
import BeamIcon, { BeamIconName } from "./BeamIcon";
import "./MessageTypes.scss";

/**
 * Surveys / diagrams message type.
 *
 * Structured like the Files card so the two read as siblings in the stream,
 * with a quick-stat strip in place of the plain size line — responses and
 * completion rate are what a survey is actually judged on.
 */

interface SurveysCardProps {
  surveys: BeamSurvey[];
  onOpen?: (survey: BeamSurvey) => void;
  onViewResults?: (survey: BeamSurvey) => void;
  onShare?: (survey: BeamSurvey) => void;
}

const FORMAT_COLORS: Record<BeamSurvey["format"], string> = {
  SURVEY: "#508afa",
  PDF: "#FF5F25",
  CSV: "#00b8b0",
};

/** The app ships a dedicated export glyph per format — use the matching one. */
const EXPORT_ICON: Record<BeamSurvey["format"], BeamIconName> = {
  SURVEY: "summary",
  PDF: "exportPdf",
  CSV: "exportCsv",
};

const SurveysCard: FC<SurveysCardProps> = ({
  surveys,
  onOpen,
  onViewResults,
  onShare,
}) => (
  <section className="mt-card mt-surveys">
    <header className="mt-card__head">
      <h4>Surveys</h4>
      <span className="mt-card__count">{surveys.length}</span>
    </header>

    <div className="mt-card__body mt-card__body--flush">
      {surveys.map((survey) => {
        const isClosed = !!survey.closedAt;

        return (
          <article key={survey.id} className="mt-survey">
            <div className="mt-survey__top">
              <span
                className="mt-survey__format"
                style={{ background: FORMAT_COLORS[survey.format] }}
              >
                .{survey.format}
              </span>
              <h5 className="mt-survey__title" title={survey.title}>
                {survey.title}
              </h5>
              <span
                className={`mt-pill ${
                  isClosed ? "mt-pill--settled" : "mt-pill--pending"
                }`}
              >
                {isClosed ? "Closed" : "Open"}
              </span>
            </div>

            <div className="mt-survey__author">
              <Avatar
                size={20}
                src={survey.authorAvatar}
                icon={<UserOutlined />}
              />
              <a href={`#/profile/${encodeURIComponent(survey.author)}`}>
                {survey.author}
              </a>
              <span className="mt-survey__sep">·</span>
              <span className="mt-survey__time">
                {moment(survey.createdAt).format("D MMM YYYY, h:mm A")}
              </span>
              <span className="mt-survey__tag">#{survey.projectTag}</span>
            </div>

            {/* quick-stat strip */}
            <div className="mt-survey__stats">
              <div className="mt-survey__stat">
                <b>{survey.responses}</b>
                <em>Responses</em>
              </div>
              <div className="mt-survey__stat mt-survey__stat--rate">
                <Progress
                  percent={survey.completionRate}
                  size="small"
                  showInfo={false}
                  strokeColor={
                    survey.completionRate >= 70 ? "#40CC52" : "#FBAE16"
                  }
                />
                <em>{survey.completionRate}% completion</em>
              </div>
              <div className="mt-survey__stat">
                <b>
                  {survey.closedAt
                    ? moment(survey.closedAt).format("D MMM")
                    : "—"}
                </b>
                <em>Date closed</em>
              </div>
            </div>

            <div className="mt-survey__actions">
              <button
                type="button"
                className="mt-btn mt-btn--primary"
                onClick={() => onOpen?.(survey)}
              >
                <BeamIcon name={EXPORT_ICON[survey.format]} size={14} />{" "}
                {survey.format === "SURVEY" ? "Open Survey" : "Export"}
              </button>
              <button
                type="button"
                className="mt-btn mt-btn--ghost"
                onClick={() => onViewResults?.(survey)}
              >
                <BarChartOutlined /> View Results
              </button>
              <Tooltip title="Copy shareable link">
                <button
                  type="button"
                  className="mt-btn mt-btn--link"
                  onClick={() => onShare?.(survey)}
                >
                  <BeamIcon name="broadcast" size={13} /> Share
                </button>
              </Tooltip>
            </div>
          </article>
        );
      })}
    </div>
  </section>
);

export default SurveysCard;
